import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Box, Button, Card, CardContent, CardMedia, Chip, CircularProgress,
  Dialog, DialogActions, DialogContent, DialogTitle, FormControl, InputLabel, Select, MenuItem,
  Grid, IconButton, Switch, TextField, Typography, Snackbar, Alert,
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from "@mui/material";
import { Add, Edit, Delete, Refresh } from "@mui/icons-material";
import { api_urls } from "../../utils/api-urls/index.js";

const API = api_urls + "api/grow";

const GrowServices = () => {
  const [services, setServices] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSvc, setEditingSvc] = useState(null);
  const [filterCategory, setFilterCategory] = useState("");
  const [form, setForm] = useState({
    category: "", name: "", nameHi: "", description: "", descriptionHi: "",
    features: "", featuresHi: "", price: "", discountPrice: "", duration: "", sortOrder: 0,
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [svcRes, catRes] = await Promise.all([
        axios.get(`${API}/services${filterCategory ? `?category=${filterCategory}` : ""}`),
        axios.get(`${API}/categories`),
      ]);
      if (svcRes.data.success) setServices(svcRes.data.data);
      if (catRes.data.success) setCategories(catRes.data.data);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to fetch data", severity: "error" });
    }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, [filterCategory]);

  const openAdd = () => {
    setEditingSvc(null);
    setForm({ category: "", name: "", nameHi: "", description: "", descriptionHi: "", features: "", featuresHi: "", price: "", discountPrice: "", duration: "", sortOrder: 0 });
    setImageFile(null);
    setImagePreview("");
    setDialogOpen(true);
  };

  const openEdit = (svc) => {
    setEditingSvc(svc);
    setForm({
      category: svc.category?._id || svc.category || "",
      name: svc.name, nameHi: svc.nameHi || "",
      description: svc.description || "", descriptionHi: svc.descriptionHi || "",
      features: (svc.features || []).join("\n"), featuresHi: (svc.featuresHi || []).join("\n"),
      price: svc.price || "", discountPrice: svc.discountPrice || "",
      duration: svc.duration || "", sortOrder: svc.sortOrder || 0,
    });
    setImageFile(null);
    setImagePreview(svc.image ? api_urls + svc.image : "");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const fd = new FormData();
    fd.append("category", form.category);
    fd.append("name", form.name);
    fd.append("nameHi", form.nameHi);
    fd.append("description", form.description);
    fd.append("descriptionHi", form.descriptionHi);
    fd.append("features", JSON.stringify(form.features.split("\n").filter(f => f.trim())));
    fd.append("featuresHi", JSON.stringify(form.featuresHi.split("\n").filter(f => f.trim())));
    fd.append("price", form.price);
    fd.append("discountPrice", form.discountPrice || 0);
    fd.append("duration", form.duration);
    fd.append("sortOrder", form.sortOrder);
    if (imageFile) fd.append("image", imageFile);

    try {
      if (editingSvc) {
        await axios.put(`${API}/services/${editingSvc._id}`, fd);
        setSnackbar({ open: true, message: "Service updated!", severity: "success" });
      } else {
        await axios.post(`${API}/services`, fd);
        setSnackbar({ open: true, message: "Service created!", severity: "success" });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || "Error saving", severity: "error" });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this service?")) return;
    try {
      await axios.delete(`${API}/services/${id}`);
      setSnackbar({ open: true, message: "Service deleted", severity: "success" });
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: "Error deleting", severity: "error" });
    }
  };

  const handleToggle = async (id) => {
    try {
      await axios.put(`${API}/services/${id}/toggle`);
      fetchData();
    } catch (err) {
      setSnackbar({ open: true, message: "Error toggling status", severity: "error" });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
  };

  if (loading) return <Box display="flex" justifyContent="center" p={5}><CircularProgress /></Box>;

  return (
    <Box p={3}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">🌱 Grow - Services</Typography>
        <Box>
          <FormControl size="small" sx={{ minWidth: 150, mr: 1 }}>
            <InputLabel>Filter Category</InputLabel>
            <Select value={filterCategory} label="Filter Category" onChange={(e) => setFilterCategory(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Button variant="outlined" startIcon={<Refresh />} onClick={fetchData} sx={{ mr: 1 }}>Refresh</Button>
          <Button variant="contained" startIcon={<Add />} onClick={openAdd} color="success">Add Service</Button>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "#f5f5f5" }}>
              <TableCell>Image</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Discount</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell>Features</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.length === 0 ? (
              <TableRow><TableCell colSpan={9} align="center">No services yet. Add one!</TableCell></TableRow>
            ) : services.map((svc) => (
              <TableRow key={svc._id}>
                <TableCell>
                  {svc.image ? <img src={api_urls + svc.image} alt={svc.name} style={{ width: 50, height: 50, borderRadius: 8, objectFit: "cover" }} /> : "-"}
                </TableCell>
                <TableCell><strong>{svc.name}</strong>{svc.nameHi ? <><br /><small>{svc.nameHi}</small></> : null}</TableCell>
                <TableCell><Chip label={svc.category?.name || "N/A"} size="small" color="primary" variant="outlined" /></TableCell>
                <TableCell><strong>₹{svc.price}</strong></TableCell>
                <TableCell>{svc.discountPrice > 0 ? `₹${svc.discountPrice}` : "-"}</TableCell>
                <TableCell>{svc.duration || "-"}</TableCell>
                <TableCell>{(svc.features || []).length} items</TableCell>
                <TableCell>
                  <Switch checked={svc.isActive} onChange={() => handleToggle(svc._id)} color="success" />
                </TableCell>
                <TableCell>
                  <IconButton onClick={() => openEdit(svc)} color="primary"><Edit /></IconButton>
                  <IconButton onClick={() => handleDelete(svc._id)} color="error"><Delete /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingSvc ? "Edit Service" : "Add Service"}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Category *</InputLabel>
                <Select value={form.category} label="Category *" onChange={(e) => setForm({ ...form, category: e.target.value })}>
                  {categories.map((c) => <MenuItem key={c._id} value={c._id}>{c.name}</MenuItem>)}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Duration" value={form.duration} onChange={(e) => setForm({ ...form, duration: e.target.value })} placeholder="e.g., 30 days, 1 month" />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Service Name *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Service Name (Hindi)" value={form.nameHi} onChange={(e) => setForm({ ...form, nameHi: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={3} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Description (Hindi)" value={form.descriptionHi} onChange={(e) => setForm({ ...form, descriptionHi: e.target.value })} multiline rows={3} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Price (₹) *" type="number" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Discount Price (₹)" type="number" value={form.discountPrice} onChange={(e) => setForm({ ...form, discountPrice: e.target.value })} helperText="Leave 0 for no discount" />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Sort Order" type="number" value={form.sortOrder} onChange={(e) => setForm({ ...form, sortOrder: e.target.value })} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Features (one per line)" value={form.features} onChange={(e) => setForm({ ...form, features: e.target.value })} multiline rows={4} placeholder={"Feature 1\nFeature 2\nFeature 3"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Features Hindi (one per line)" value={form.featuresHi} onChange={(e) => setForm({ ...form, featuresHi: e.target.value })} multiline rows={4} placeholder={"फीचर 1\nफीचर 2\nफीचर 3"} />
            </Grid>
            <Grid item xs={12}>
              <Button variant="outlined" component="label" fullWidth>
                {imageFile ? "Change Image" : "Upload Service Image"}
                <input type="file" hidden accept="image/*" onChange={handleImageChange} />
              </Button>
              {imagePreview && <Box mt={1}><img src={imagePreview} alt="preview" style={{ width: 120, height: 80, objectFit: "cover", borderRadius: 8 }} /></Box>}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name || !form.category || !form.price} color="success">Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={3000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: "bottom", horizontal: "right" }}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default GrowServices;
